/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Row, Col, Visible } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import { useOrganizationCostQuery } from "~/generated/graphql";
import { BrickLoader, UniversalWrap } from "~/components";
import { CostPerProviderWidget } from "./CostPerProviderWidget";
import { DayToDayWidget } from "./DayToDayWidget";
import { ForecastWidget } from "./ForecastWidget";

type Props = {
  isCurrentBillingPeriod: boolean;
};

export function GlobalCosts({ isCurrentBillingPeriod }: Props) {
  const { data, loading, error } = useOrganizationCostQuery();

  const columnSize = isCurrentBillingPeriod ? 4 : 6;

  const hasCosts = data?.organization?.cost != null;
  const currentMonthCost = getCost(data?.organization?.cost?.currentMonth);

  return (
    <Row align="flex-start">
      <UniversalWrap
        loaderProps={{
          loading,
          Component: BrickLoader,
        }}
        errorProps={{
          error,
        }}
      >
        <When condition={isCurrentBillingPeriod}>
          <Col lg={4} md={4} sm={4}>
            <DayToDayWidget
              current={currentMonthCost}
              previous={getCost(
                data?.organization?.cost?.previousMonthDayToDay
              )}
            />
          </Col>
        </When>
        <Col lg={columnSize}>
          <When condition={hasCosts}>
            <Visible xs>
              <Spacer height={theme.spacing.spacing07} width="100%" />
            </Visible>
            <ForecastWidget
              showForecast={isCurrentBillingPeriod}
              currentMonth={currentMonthCost}
              previous={getCost(data?.organization?.cost?.previousMonth)}
              forecast={
                data?.organization?.cost?.forecast
                  ? parseFloat(data?.organization?.cost?.forecast)
                  : undefined
              }
            />
          </When>
        </Col>
        <Visible xs sm md>
          <Spacer height={theme.spacing.spacing07} width="100%" />
        </Visible>
        <Col lg={columnSize}>
          <CostPerProviderWidget />
        </Col>
      </UniversalWrap>
    </Row>
  );
}

function getCost(value: string | null | undefined): number {
  return parseFloat(value ?? "0");
}
